import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
  CustomSelectProps,
  ErrorStyles,
  DisabledStyles,
  getSelectedCSS,
} from "./Select";
import { capitaliseFirstLetter, InputVariantTypes } from "@ecologi/shared";
import {
  BackgroundColourMapper,
  ErrorMessage,
  HelpMessage,
} from "../TextField/TextField.styles";
import DownArrowBase from "@ecologi/assets/icons/FormFields/down_arrow.svg";
import { theme } from "../theme";

type NativeSelectProps = {
  items: { label: string; value: string }[];
  onChange: (event: any) => void;
  value: string;
  label: string;
  name: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  variant?: InputVariantTypes;
  errorText?: string | false;
  helpText?: string;
  hiddenLabel?: boolean;
};

type SelectStyleProps = {
  populated?: boolean | null;
} & CustomSelectProps;

const Label = styled.label<{ isDisabled?: boolean }>`
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  margin-bottom: 10px;
  color: ${(props) => props.isDisabled && "#c9d0d8"};
`;

const Select = styled.select<SelectStyleProps>`
  border-bottom: 2px solid ${(props) => props.theme.colors.cloudDarker};
  border-left: none;
  border-right: none;
  border-top: 2px solid transparent;
  color: ${(props) =>
    props.populated ? props.theme.colors.charcoal : "#9fa6ad"};
  cursor: pointer;
  font-size: 18px;
  height: 48px;
  line-height: 2;
  margin-bottom: 10px;
  overflow: hidden;
  padding: 0px 16px;
  white-space: nowrap;
  width: 100%;

  &:focus {
    outline: none;
    color: ${(props) => props.theme.colors.charcoal};

    & + .arrow {
      transform: rotate(180deg);
    }
  }

  background-color: ${(props) =>
    props.variant
      ? BackgroundColourMapper[props.variant]
      : BackgroundColourMapper.DEFAULT};

  ${(props) => props.disabled && DisabledStyles};
  ${(props) => props.isInvalid && ErrorStyles};

  ${(props) =>
    !props.isDisabled &&
    css`
      &:hover {
        background-color: ${theme.colors.mistDark};
      }
    `}

  ${(props) => props.isFocused && !props.isDisabled && getSelectedCSS(props)};
  ${(props) => props.isInvalid && ErrorStyles};

  -webkit-appearance: none;
  -moz-appearance: none;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const DownArrow = styled(DownArrowBase)<SelectStyleProps>`
  position: absolute;
  right: 16px;
  top: 22px;

  path {
    stroke: ${(props) =>
      (props.isInvalid && props.theme.colors.scarlet) ||
      (props.isDisabled && "#9fa6ad")};
  }
`;

export const NativeSelect = ({
  items,
  onChange,
  value,
  label,
  name,
  isDisabled,
  isInvalid,
  variant,
  errorText,
  helpText,
  hiddenLabel,
}: NativeSelectProps) => {
  return (
    <>
      {!hiddenLabel && <Label>{label}</Label>}
      <SelectWrapper>
        <Select
          onChange={(e) => {
            onChange(e);
          }}
          name={name}
          value={value}
          disabled={isDisabled}
          isInvalid={isInvalid}
          variant={variant}
          populated={value !== "" || null}
        >
          {
            //filter out any empty string values
            items
              .filter((item) => item.value !== "")
              .map((item: any) => (
                <option key={item.label} value={item.value}>
                  {capitaliseFirstLetter(item.label, true)}
                </option>
              ))
          }
        </Select>
        <DownArrow
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          className="arrow"
        />
      </SelectWrapper>
      {isInvalid && (
        <ErrorMessage use="label" as="label">
          {errorText ? errorText : `Please select ${label}`}
        </ErrorMessage>
      )}
      {!isInvalid && helpText && (
        <HelpMessage use="label" as="label">
          {helpText}
        </HelpMessage>
      )}
    </>
  );
};
